import { useState } from "react";
import AppLogo from "./AppLogo";
import { C, FONT, Reveal } from "./ui/theme";
import { PillButton, marketingButtonStyles } from "./ui/Button";

// Exported so the public marketing landing page can render these same
// plans/prices in its Pricing section (single source of truth — update
// pricing here and both surfaces stay in sync automatically).
//
// NOTE: this data is untouched by the visual redesign below — prices,
// features, ids, cta labels and badges are all exactly what they were.
// Only how they're presented has changed.
export const plans = [
  { id: "trial", name: "Free Trial", badge: "START HERE", badgeColor: "#16A34A", price: 0, leads: "2,000", seats: 10, trial: true, cta: "Start Free Trial", features: ["Full Growth plan access","2,000 leads included","AI email sequences","Outlook integration","LinkedIn outreach","Reply AI routing","Priority support","No credit card required"] },
  { id: "starter", name: "Starter", price: 49, leads: "500", seats: 2, cta: "Choose Starter", features: ["500 leads/month","2 seats included","Apollo lead discovery","Email sequences","Basic analytics","Email support"] },
  { id: "growth", name: "Growth", badge: "MOST POPULAR", badgeColor: "#6366F1", price: 149, leads: "2,000", seats: 10, popular: true, cta: "Choose Growth", features: ["2,000 leads/month","10 seats included","Everything in Starter","LinkedIn outreach","Reply AI routing","Outlook Calendar sync","Pipeline management","Priority support"] },
  { id: "scale", name: "Scale", price: 399, leads: "10,000", seats: 50, cta: "Choose Scale", features: ["10,000 leads/month","50 seats included","Everything in Growth","Custom AI personas","API access","Dedicated CSM","SLA 99.9%","White-label options"] },
];

// Short, plain-language positioning line under each plan name — purely
// presentational (not part of the shared `plans` data, so it can't affect
// the landing page's pricing section or any billing logic). Helps a reader
// place themselves on the ladder in under five seconds.
const PLAN_POSITIONING = {
  trial: "Try SDR ReachOut",
  starter: "Individual & small teams",
  growth: "Growing SDR teams",
  scale: "Larger revenue orgs",
};

function CheckIcon({ tone = C.accent }) {
  return (
    <span aria-hidden style={{ width: 18, height: 18, borderRadius: "50%", background: tone + "17", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
        <path d="M2.2 6.3L4.6 8.7L9.8 3.2" stroke={tone} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function TrustIcon({ name }) {
  const common = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    shield: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />,
    refresh: <><path d="M20 11a8 8 0 10-1.6 4.8" /><path d="M20 5v6h-6" /></>,
    clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
    chat: <path d="M4 5h16v11H8l-4 4V5z" />,
  };
  return <svg {...common} aria-hidden>{paths[name]}</svg>;
}

// Same background wash used across the marketing surfaces — applied to
// html/body too (not just .plan-page) so mobile's overscroll bounce
// doesn't reveal a plain gap. Replaces the previous full-photo cover with
// a calmer, brighter atmospheric-gradient treatment: closer to how a
// modern checkout/onboarding surface (Stripe, Linear) reads than a stock
// photo ever could, and matches the "bright, minimal, spacious" brief.
const planPageStyles = `
  *{box-sizing:border-box}
  html, body {
    min-height: 100%;
    min-height: 100dvh;
    background: ${C.bgAlt};
  }
  .plan-page {
    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    font-family: ${FONT};
    color: ${C.text};
    -webkit-font-smoothing: antialiased;
    background: ${C.bgAlt};
    overflow: hidden;
  }
  .plan-glow { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
  .plan-glow::before, .plan-glow::after {
    content: ""; position: absolute; border-radius: 50%; filter: blur(90px);
  }
  .plan-glow::before { width: 620px; height: 620px; top: -220px; left: -160px; background: radial-gradient(circle, rgba(0,113,227,.16), transparent 70%); }
  .plan-glow::after { width: 560px; height: 560px; bottom: -240px; right: -140px; background: radial-gradient(circle, rgba(88,86,214,.14), transparent 70%); }

  .plan-top, .plan-main { position: relative; z-index: 1; }
  .plan-top {
    padding: 22px 6%;
    display: flex; align-items: center; justify-content: space-between;
  }
  .plan-logo-btn { background: none; border: none; padding: 4px; margin: -4px; cursor: pointer; display: inline-flex; align-items: center; gap: 10px; border-radius: 8px; transition: opacity .15s ease; }
  .plan-logo-btn:hover { opacity: .8; }
  .plan-logo-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 4px; }
  .plan-main { flex: 1; padding: 12px 6% 80px; display: flex; justify-content: center; }

  .plan-hero { text-align: center; max-width: 640px; margin: 0 auto 36px; }
  .plan-eyebrow { display: inline-flex; align-items: center; gap: 6px; font-size: 13.5px; font-weight: 600; color: ${C.accent}; background: ${C.accentMuted}; padding: 6px 14px; border-radius: 999px; margin-bottom: 18px; }
  .plan-h1 { font-size: clamp(28px, 3.6vw, 40px); font-weight: 700; letter-spacing: -1px; color: ${C.text}; margin-bottom: 12px; line-height: 1.1; }
  .plan-sub { font-size: 16px; color: ${C.textSub}; line-height: 1.55; margin-bottom: 16px; }
  .plan-trust-mini { font-size: 12.5px; color: ${C.textFaint}; letter-spacing: .1px; }

  /* Segmented Monthly/Annual control */
  .plan-segmented-wrap { display: flex; justify-content: center; margin-bottom: 40px; }
  .plan-segmented { position: relative; display: grid; grid-template-columns: 1fr 1fr; align-items: center; background: #fff; border: 1px solid ${C.borderSoft}; border-radius: 999px; padding: 4px; box-shadow: 0 1px 2px rgba(16,24,40,.04); }
  .plan-seg-highlight { position: absolute; top: 4px; bottom: 4px; left: 4px; width: calc(50% - 4px); background: ${C.bgAlt}; border-radius: 999px; box-shadow: inset 0 0 0 1px ${C.borderSoft}; transition: transform .28s cubic-bezier(.32,.72,0,1); z-index: 0; }
  .plan-seg-btn { position: relative; z-index: 1; display: inline-flex; align-items: center; gap: 8px; justify-content: center; background: none; border: none; padding: 9px 22px; border-radius: 999px; font-family: ${FONT}; font-size: 13.5px; font-weight: 600; color: ${C.textSub}; cursor: pointer; transition: color .2s ease; white-space: nowrap; }
  .plan-seg-btn.is-active { color: ${C.text}; }
  .plan-seg-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
  .plan-save-pill { font-size: 10.5px; font-weight: 700; color: ${C.green}; background: ${C.greenMuted}; padding: 2px 8px; border-radius: 999px; letter-spacing: .2px; }

  .plan-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; align-items: stretch; }
  @media (max-width: 1080px) { .plan-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  @media (max-width: 620px) { .plan-grid { grid-template-columns: 1fr; max-width: 420px; margin: 0 auto; } }

  .plan-card {
    position: relative; display: flex; flex-direction: column; height: 100%;
    background: #fff; border: 1px solid ${C.borderSoft}; border-radius: 20px; padding: 30px 26px;
    box-shadow: 0 1px 2px rgba(16,24,40,.04);
    transition: transform .22s cubic-bezier(.22,.61,.36,1), box-shadow .22s cubic-bezier(.22,.61,.36,1), border-color .22s ease;
    cursor: pointer;
  }
  .plan-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(16,24,40,.09); border-color: ${C.border}; }
  .plan-card.is-popular {
    border: 1.5px solid ${C.accent};
    box-shadow: 0 4px 10px rgba(0,113,227,.08), 0 24px 48px rgba(0,113,227,.14);
    transform: translateY(-8px);
  }
  .plan-card.is-popular:hover { transform: translateY(-11px); box-shadow: 0 6px 14px rgba(0,113,227,.1), 0 28px 56px rgba(0,113,227,.18); }
  @media (max-width: 1080px) { .plan-card.is-popular, .plan-card.is-popular:hover { transform: translateY(-3px); } }

  .plan-badge { position: absolute; top: -13px; left: 26px; display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 700; letter-spacing: .4px; padding: 5px 12px; border-radius: 999px; color: #fff; }
  .plan-badge.badge-popular { background: linear-gradient(120deg, ${C.accent}, ${C.purple}); box-shadow: 0 4px 10px rgba(0,113,227,.3); }
  .plan-badge.badge-start { background: ${C.text}; }

  .plan-kicker { font-size: 12px; font-weight: 600; color: ${C.textFaint}; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px; }
  .plan-name { font-size: 18px; font-weight: 700; color: ${C.text}; letter-spacing: -.2px; margin-bottom: 14px; }

  .plan-price { font-size: 36px; font-weight: 750; color: ${C.text}; letter-spacing: -1px; display: inline-block; animation: priceMorph .32s cubic-bezier(.22,.61,.36,1); }
  .plan-price-period { font-size: 13px; color: ${C.textSub}; margin-left: 3px; }
  @keyframes priceMorph { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
  .plan-price-sub { font-size: 12.5px; color: ${C.textSub}; margin-top: 4px; margin-bottom: 20px; min-height: 16px; }

  .plan-divider { height: 1px; background: ${C.borderSoft}; margin-bottom: 18px; }
  .plan-features { list-style: none; margin: 0 0 24px; flex: 1; display: flex; flex-direction: column; gap: 10px; }
  .plan-feature { font-size: 13.5px; color: ${C.textSub}; display: flex; align-items: flex-start; gap: 9px; line-height: 1.4; }
  .plan-cta-wrap { margin-top: auto; }

  .plan-trust-row { display: flex; justify-content: center; align-items: center; gap: 28px; margin-top: 44px; flex-wrap: wrap; }
  .plan-trust-item { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; color: ${C.textSub}; }
  .plan-trust-item svg { color: ${C.textFaint}; flex-shrink: 0; }

  @media (max-width: 640px) {
    .plan-top { padding: 18px 5%; }
    .plan-main { padding: 8px 5% 64px; }
    .plan-hero { margin-bottom: 28px; }
    .plan-trust-row { gap: 14px 22px; margin-top: 36px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .plan-card, .plan-card:hover, .plan-card.is-popular, .plan-card.is-popular:hover,
    .plan-seg-highlight, .plan-toggle-thumb, .plan-price, .plan-logo-btn {
      transition: none !important; animation: none !important; transform: none !important;
    }
    .plan-card.is-popular { transform: translateY(-8px) !important; }
  }
`;

export default function PlanSelectionPage({ onSelect, user }) {
  const [annual, setAnnual] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [payForm, setPayForm] = useState({ name: "", card: "", expiry: "", cvv: "", email: "" });
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState("");

  const handleSelect = (plan) => {
    if (plan.trial) { setSelected(plan.id); setLoading(true); setTimeout(() => { onSelect(plan.id); setLoading(false); }, 600); }
    else { setPaymentPlan(plan); setShowPayment(true); }
  };

  const handlePayment = (e) => {
    e.preventDefault();
    if (!payForm.name || !payForm.card || !payForm.expiry || !payForm.cvv || !payForm.email) { setPayError("Please fill in all fields"); return; }
    setPayLoading(true); setPayError("");
    setTimeout(() => { setPayLoading(false); setSelected(paymentPlan.id); setShowPayment(false); onSelect(paymentPlan.id); }, 1500);
  };

  const formatCard = (v) => v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
  const formatExpiry = (v) => { const d=v.replace(/\D/g,"").slice(0,4); return d.length>2?d.slice(0,2)+"/"+d.slice(2):d; };
  const price = (p) => p.trial ? "FREE" : annual ? Math.round(p.price*0.8) : p.price;

  return (
    <div className="plan-page">
      <style>{planPageStyles}{marketingButtonStyles}</style>
      <div className="plan-glow" aria-hidden />

      <div className="plan-top">
        <button type="button" className="plan-logo-btn" onClick={() => window.location.reload()}>
          <AppLogo size={30} />
          <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>SDR ReachOut</span>
        </button>
      </div>

      <div className="plan-main">
        <div style={{ width: "100%", maxWidth: 1180 }}>
          <Reveal>
            <div className="plan-hero">
              <span className="plan-eyebrow">Welcome to SDR ReachOut, {user?.firstName || "there"} 👋</span>
              <h1 className="plan-h1">Choose your growth plan</h1>
              <p className="plan-sub">Start free. Scale when you're ready.</p>
              <p className="plan-trust-mini">No credit card required &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Upgrade whenever you're ready</p>
            </div>
          </Reveal>

          <div className="plan-segmented-wrap">
            <div className="plan-segmented" role="group" aria-label="Billing period">
              <div className="plan-seg-highlight" style={{ transform: annual ? "translateX(100%)" : "translateX(0%)" }} />
              <button type="button" className={"plan-seg-btn" + (!annual ? " is-active" : "")} aria-pressed={!annual} onClick={() => setAnnual(false)}>
                Monthly
              </button>
              <button type="button" className={"plan-seg-btn" + (annual ? " is-active" : "")} aria-pressed={annual} onClick={() => setAnnual(true)}>
                Annual <span className="plan-save-pill">Save 20%</span>
              </button>
            </div>
          </div>

          <div className="plan-grid">
            {plans.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07}>
                <div
                  className={"plan-card" + (p.popular ? " is-popular" : "")}
                  onClick={() => !loading && handleSelect(p)}
                  role="group"
                  aria-label={p.name + " plan"}
                >
                  {p.badge && (
                    <span className={"plan-badge " + (p.popular ? "badge-popular" : "badge-start")}>{p.badge}</span>
                  )}
                  <p className="plan-kicker">{PLAN_POSITIONING[p.id]}</p>
                  <p className="plan-name">{p.name}</p>

                  <div>
                    <span className="plan-price" key={p.trial ? "free" : (annual ? "a" : "m")}>
                      {p.trial ? "Free" : "$" + price(p)}
                    </span>
                    {!p.trial && <span className="plan-price-period">/month</span>}
                  </div>
                  <p className="plan-price-sub">
                    {p.trial ? `${p.leads} leads included, then choose a plan` : annual ? "Billed annually" : "or save 20% billed annually"}
                  </p>

                  <div className="plan-divider" />

                  <ul className="plan-features">
                    {p.features.slice(0, 6).map(f => (
                      <li key={f} className="plan-feature">
                        <CheckIcon tone={p.popular ? C.accent : C.textSub} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="plan-cta-wrap">
                    <PillButton
                      variant={p.popular ? "primary" : "secondary"}
                      size="md"
                      loading={loading && selected === p.id}
                      onClick={(e) => { e.stopPropagation(); if (!loading) handleSelect(p); }}
                      style={{ width: "100%" }}
                    >
                      {loading && selected === p.id ? "Starting..." : p.cta}
                    </PillButton>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="plan-trust-row">
            <span className="plan-trust-item"><TrustIcon name="shield" />No credit card required</span>
            <span className="plan-trust-item"><TrustIcon name="refresh" />Cancel anytime</span>
            <span className="plan-trust-item"><TrustIcon name="clock" />Setup in 2 minutes</span>
            <span className="plan-trust-item"><TrustIcon name="chat" />24/7 support</span>
          </div>
        </div>
      </div>

      {showPayment && paymentPlan && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
          <style>{`
            .pay-input { width: 100%; border: 1.5px solid ${C.border}; border-radius: 10px; padding: 11px 14px; font-size: 14px; outline: none; font-family: ${FONT}; color: ${C.text}; transition: border-color .15s ease, box-shadow .15s ease; background: #fff; }
            .pay-input:focus { border-color: ${C.accent}; box-shadow: 0 0 0 3px ${C.accentMuted}; }
            .pay-input::placeholder { color: ${C.textFaint}; }
            .pay-close-btn { background: ${C.bgAlt}; border: none; border-radius: 8px; width: 34px; height: 34px; font-size: 17px; color: ${C.textSub}; cursor: pointer; transition: background .15s ease; }
            .pay-close-btn:hover { background: ${C.borderSoft}; }
            .pay-close-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
            .pay-submit-btn { width: 100%; margin-top: 22px; padding: 14px 0; border-radius: 12px; border: none; background: ${C.accent}; color: #fff; font-size: 15px; font-weight: 700; cursor: pointer; transition: background .15s ease, opacity .15s ease, transform .12s ease; font-family: ${FONT}; }
            .pay-submit-btn:hover:not(:disabled) { background: ${C.accentHover}; }
            .pay-submit-btn:active:not(:disabled) { transform: scale(.98); }
            .pay-submit-btn:disabled { opacity: .7; cursor: default; }
            .pay-submit-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 3px; }
            .pay-label { font-size: 12px; font-weight: 600; color: ${C.textSub}; margin-bottom: 6px; display: block; }
          `}</style>
          <div style={{ background: "#fff", borderRadius: 20, padding: 36, maxWidth: 460, width: "100%", boxShadow: "0 30px 80px rgba(16,24,40,.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
              <div>
                <h2 style={{ fontSize: 21, fontWeight: 700, color: C.text, marginBottom: 4, letterSpacing: "-.3px" }}>Complete your purchase</h2>
                <p style={{ fontSize: 13.5, color: C.textSub }}>{paymentPlan.name} &middot; ${annual ? Math.round(paymentPlan.price * 0.8) : paymentPlan.price}/mo</p>
              </div>
              <button className="pay-close-btn" onClick={() => setShowPayment(false)} aria-label="Close">&#215;</button>
            </div>
            {payError && <div style={{ background: C.redMuted, border: `1px solid ${C.red}40`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.red }}>{payError}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[["Full Name", "name", "John Smith", "text"], ["Email", "email", "john@company.com", "email"]].map(([l, k, ph, t]) => (
                <div key={k}>
                  <label className="pay-label">{l}</label>
                  <input className="pay-input" value={payForm[k]} onChange={e => setPayForm(p => ({ ...p, [k]: e.target.value }))} placeholder={ph} type={t} />
                </div>
              ))}
              <div>
                <label className="pay-label">Card Number</label>
                <input className="pay-input" value={payForm.card} onChange={e => setPayForm(p => ({ ...p, card: formatCard(e.target.value) }))} placeholder="4242 4242 4242 4242" maxLength={19} style={{ fontFamily: "monospace" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label className="pay-label">Expiry</label>
                  <input className="pay-input" value={payForm.expiry} onChange={e => setPayForm(p => ({ ...p, expiry: formatExpiry(e.target.value) }))} placeholder="MM/YY" maxLength={5} />
                </div>
                <div>
                  <label className="pay-label">CVV</label>
                  <input className="pay-input" value={payForm.cvv} onChange={e => setPayForm(p => ({ ...p, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="123" maxLength={4} />
                </div>
              </div>
            </div>
            <button className="pay-submit-btn" onClick={handlePayment} disabled={payLoading}>
              {payLoading ? "Processing..." : "Pay $" + (annual ? Math.round(paymentPlan.price * 0.8) : paymentPlan.price) + "/mo"}
            </button>
            <p style={{ textAlign: "center", fontSize: 12, color: C.textFaint, marginTop: 14 }}>Secured by SSL &middot; Cancel anytime</p>
          </div>
        </div>
      )}
    </div>
  );
}
